import {_, classNames, React, observer, action} from '@api'
import {Spinner} from '@blueprintjs/core'
import {SpecificationItem, useBuilderCardStore, CardFieldStoreContext, BuilderCardFieldStore, useCardFieldStore} from './'
import {toJS} from 'mobx'
import * as css from './BuilderCardField.css'
import {ICatalogFieldConfig, ISectionConfig, DetailsViewFieldConfig, useCatalog} from '@stores'
import {CheckboxInput} from 'centric-ui-components'
import {CatalogFieldRenderer} from '@lazy'
import {ErrorBoundary, useVipLocalStore} from '@design-board'

export const BuilderCardField = observer<{ f: ICatalogFieldConfig, isPopover?: boolean, className?: string }, any>(function BuilderCardField({f, className, ...props}, ref) {
  const cardStore = useBuilderCardStore(),
    {props: {disableSelection}} = cardStore,
    isPopover = props.isPopover ?? cardStore?.props?.isPopover,
    store = useVipLocalStore((ws) => new BuilderCardFieldStore(ws, f, cardStore)),
    {
      catalog: {idFor, settings: {showCardLabels, groupSectionFieldsOnCard}},
      showNestedCardItems,
      dragHandlers, isFieldNested, field: {field, card}, style, showFieldTitle, sectionConfig, isDirty, editable, value, title, titleComponent, textValue, valueComponent, onContextMenu
    } = store

  if (sectionConfig && groupSectionFieldsOnCard[sectionConfig.id]) return null
  //if (perspective && nested?.data && perspective.field == f.field) return null // NVS not sure about this

  //console.log(`BuilderCardField()`, toJS({f, value, textValue, showFieldTitle}, {recurseEverything: true}))
  return (
    <CardFieldStoreContext.Provider value={store}>
      <ErrorBoundary>
        <SpecificationItem
          ref={ref}
          key={field}
          showTitle={showCardLabels && showFieldTitle} // For booleans we will show a switch component with its own label
          data-field={field}
          editable={editable}
          data-dirty={isDirty}
          data-editable={editable}
          data-id={f.esIndex ? (_.isArray(value.val) ? value.val.map(child => idFor(child)).join(',') : idFor(value.val)) : value.val}
          style={style}
          data-nested={isFieldNested === true}
          placeholderText={title[0]}
          draggable={!isPopover && !disableSelection}
          className={classNames(className, css.cardField, {[css.showLabel]: showCardLabels, [css.titledField]: card?.titleFields?.length > 0, [css.multiline]: card?.multiline})}
          field={f}
          info={isFieldNested ? value?.val : null}
          {...dragHandlers}
          title={titleComponent}
          valueComponent={valueComponent}
          valueTitle={isFieldNested ? null : textValue}
          onContextMenu={onContextMenu}
          isPopover={isPopover}
          noPopover={!showNestedCardItems}
        />
      </ErrorBoundary>
    </CardFieldStoreContext.Provider>)
}, {forwardRef: true})

export const SectionalCardFieldRenderer = observer<{
  field: ICatalogFieldConfig,
  sectionConfig?: ISectionConfig
  parent?: any
  className?: string,
  readOnly?: boolean
}>(function SectionalCardFieldRenderer({field, sectionConfig, sectionConfig: {path, viewOptions}, readOnly = false}) {
  const {strings, data, groupByStore} = useBuilderCardStore(),
    {editable} = useCardFieldStore(),
    catalog = useCatalog()

  let nestedData = _.get(data, path) ?? []

  // Remove any nested fields that are filtered out by our group hierarchy
  if (_.isArray(nestedData) && groupByStore) {
    const prefix = `${path}.`,
      nestedKeys = _.keys(groupByStore.keysIncludingPath).filter(k => k.startsWith(prefix))

    if (nestedKeys.length) {
      const filteredNestedData = nestedData.filter(d => {
        return nestedKeys.every(k => {
          const val = _.get(d, k.substring(prefix.length))
          return val == groupByStore.keysIncludingPath[k] || (val === null && groupByStore.keysIncludingPath[k] === false)
        })
      })

      //console.log(toJS({nestedData, filteredNestedData}, {recurseEverything: true}))
      nestedData = filteredNestedData
    }
  }

  const dict = _.keyBy(nestedData, v => _.get(v, viewOptions.field))

  //console.log({field, viewOptions, path, array: toJS(array), dict: toJS(dict), data: toJS(data)})

  if (!catalog.sectionValues.has(sectionConfig.id)) return <Spinner size={20}/>

  let visibleValues = catalog.sectionValues.get(sectionConfig.id)?.filter(({key}) => {
    const lookup = `${path}.${viewOptions.field}.${key}`

    return catalog.cardView.all?.show || catalog.cardView[lookup]?.show
  })

  if (!visibleValues.length) {
    visibleValues = catalog.sectionValues.get(sectionConfig.id)
  }

  // if (!visibleValues.length) return <span className={css.noSectionValuesSelected}>No Selected {pluralize(sectionConfig.label)}</span>

  const fieldPath = field.field.substring(path.length ? path.length + 1 : 0),
    contents = visibleValues?.filter(({key}) => dict[key])?.map(({key}) => <SectionalCardFieldRendererIndividual key={key} name={key} fieldPath={fieldPath} item={dict[key]} field={field} sectionConfig={sectionConfig} readOnly={readOnly}/>)

  return (!contents
          ? null
          : <div className={css.sectionalFieldValues} data-type={field?.def?.type} data-editable={editable} data-count={visibleValues.length}>
            {contents.every(v => v === null) ? strings.emptyLabel : contents}
          </div>)
})
SectionalCardFieldRenderer.displayName = 'SectionalCardFieldRenderer'

export const SectionalCardFieldRendererIndividual = observer<{
  field: ICatalogFieldConfig,
  name: string,
  sectionConfig?: ISectionConfig
  parent?: any,
  item: any,
  fieldPath?: string,
  className?: string,
  readOnly?: boolean
}>(function SectionalCardFieldRendererIndividual({field, name, sectionConfig, fieldPath, item, sectionConfig: {path, viewOptions}, readOnly = false}) {
  const {strings, editable: isEditable, stores, cardStore: {data: cardData, workspace, groupByStore, isOffline}, isUpdateOnAssignment, updateOnAssignmentData, updateOnAssignmentField} = useCardFieldStore(),
    value = _.get(item, fieldPath),
    data = cardData,
    editable = _.isFunction(field.editable) ? field.editable({data, stores, secondary: item, isPublished: !isOffline}) : isEditable,
    showLabel = !groupByStore || !viewOptions?.field || !_.keys(groupByStore?.keysIncludingPath).includes(`${path}.${viewOptions.field}`)

  // console.log({key, field, fieldPath, value})

  switch (field.def?.type) {
    case 'Boolean': {
      return <CheckboxInput
        className={css.checkbox}
        disabled={!editable || readOnly}
        name={name}
        label={showLabel ? name : null}
        checked={value}
        onChange={action(e => {
          //console.log(`toggleCheckbox`, {key, fieldPath, value, nestedData: toJS(nestedData, {recurseEverything: true})})
          //console.log('NYI - secondary editing')
          if (isUpdateOnAssignment && !_.isArray(updateOnAssignmentField)) {
            _.merge(data, updateOnAssignmentData(item, fieldPath, !value))
          } else {
            _.set(item, fieldPath, !value)
          }
        })}
      />
    }

    case 'DateTime': {
      return <div className={css.sectionFieldValue}>
        {showLabel && <label>{name}</label>}
        <span className={css.value}>{value ? new Date(value).toLocaleDateString() : strings.emptyLabel}</span>
      </div>
    }

    default: {
      // if (value === undefined || value === null || value === '') {
      //   return null
      // }

      return <div className={css.sectionFieldValue} data-key={name} data-value={value}>
        {showLabel && <label>{name}</label>}
        <span className={css.value}>
          {editable && !readOnly
           ? <CatalogFieldRenderer workspace={workspace} className={css.fieldRenderer} section={{id: name}} field={field as Partial<DetailsViewFieldConfig>} nestedData={item}/>
           : value === undefined || value === null || value === '' ? strings.emptyLabel : value}</span>
      </div>
    }
  }
})



// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/BuilderCardField.tsx